import React from 'react';

import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material';

import { DeleteIcon, FileTypeIcon } from '@components/icons';
import { useNumbro } from '@utils/hooks';

import type { SelectedFilePreviewProperties } from '.';

export const SelectedFilePreview: React.FC<SelectedFilePreviewProperties> = ({
  dataTest = 'selected_file_preview',
  file,
  onDelete,
  isDisabled = false,
  uploadProgress = 0,
  sx,
}) => {
  const numbro = useNumbro();

  const isUploading = uploadProgress > 0;

  const fileSize = React.useMemo(() => {
    return numbro(file.size).format({
      mantissa: 2,
      average: true,
      output: 'byte',
      base: 'binary',
    });
  }, [file.size, numbro]);

  const uploadedSize = React.useMemo(() => {
    if (!isUploading) return '';
    const loaded = Math.round((uploadProgress / 100) * file.size);
    return numbro(loaded).format({
      mantissa: 2,
      average: true,
      output: 'byte',
      base: 'binary',
    });
  }, [file.size, uploadProgress, isUploading, numbro]);

  return (
    <Box data-testid={dataTest} sx={sx}>
      <Card variant="outlined" sx={{ position: 'relative', overflow: 'hidden' }}>
        <CardContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 2,
            '&:last-child': { pb: 2 },
          }}
        >
          <FileTypeIcon
            fileType={file.type}
            sx={{ mr: 2 }}
            styles={{ fontSize: '3rem' }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minWidth: 0
            }}>
            <Typography
              data-testid={`${dataTest}_filename`}
              sx={{
                fontSize: "1rem",
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              {file.name}
            </Typography>
            <Typography
              data-testid={`${dataTest}_filesize`}
              variant="body2"
              color={isUploading ? 'primary' : 'text.secondary'}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                fontWeight: isUploading ? 500 : 400,
              }}
            >
              {isUploading && (
                <CircularProgress size={14} thickness={5} />
              )}
              {isUploading
                ? `${uploadProgress}% · ${uploadedSize} / ${fileSize}`
                : fileSize
              }
            </Typography>
          </Box>
          <IconButton
            data-testid={`${dataTest}_delete_button`}
            color="error"
            onClick={onDelete}
            disabled={isDisabled}
            sx={{ ml: 1 }}
          >
            <DeleteIcon sx={{ fontSize: '1.5rem' }} variant="thin" isDisabled={isDisabled} />
          </IconButton>
        </CardContent>
        {isUploading && (
          <LinearProgress
            data-testid={`${dataTest}_upload_progress`}
            variant="determinate"
            value={uploadProgress}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 3,
            }}
          />
        )}
      </Card>
    </Box>
  );
};
